import { parseGermanNumber } from '../../../utils/parseGermanNumber';
import type { Material } from '../types/material';

export const VAT_OPTIONS = [
  { value: '19', label: '19 %' },
  { value: '7', label: '7 %' },
  { value: '0', label: '0 %' },
];

export const MATERIAL_UNIT_OPTIONS = [
  { value: '', label: 'Bitte wählen…' },
  { value: 'Stk', label: 'Stk' },
  { value: 'm', label: 'm' },
  { value: 'm²', label: 'm²' },
  { value: 'm³', label: 'm³' },
  { value: 'kg', label: 'kg' },
  { value: 'l', label: 'l' },
];

export function calcMaterialVk(ek: string, surchargePct: string): number {
  const ekNum = parseGermanNumber(ek);
  const sur = parseGermanNumber(surchargePct);
  return ekNum * (1 + sur / 100);
}

const DEFAULT_OMD_SLUGS = new Set(['g-c']);

/** Shared catalog rows whose Großhändler has Open Master Data enabled (env and/or DB). */
export function isOmdCatalogMaterial(
  m: { kind?: string | null; supplier_slug?: string | null },
  enabledSlugs: Iterable<string> = DEFAULT_OMD_SLUGS,
): boolean {
  if (m.kind !== 'shared') return false;
  const slug = String(m.supplier_slug ?? '').trim().toLowerCase();
  if (!slug) return false;
  for (const enabled of enabledSlugs) {
    if (enabled.toLowerCase() === slug) return true;
  }
  return false;
}

export function materialToEditDraft(m: Material): Partial<Material> {
  const sur = typeof m.surcharge_percent === 'number' ? m.surcharge_percent : 0;
  const vatRaw = m.vat_percent;
  const vat = vatRaw === 0 || vatRaw === 7 || vatRaw === 19 ? vatRaw : 19;
  return { ...m, surcharge_percent: sur, vat_percent: vat, minutes: m.minutes ?? null };
}

export function formatMaterialPrice(value: number | null) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';
  const formatted = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  return `${formatted}€`;
}

export function validateMaterialSurcharge(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}

export function normalizeMaterialVat(v: number | null | undefined): 0 | 7 | 19 {
  return v === 0 || v === 7 || v === 19 ? v : 19;
}

export function formatMaterialVatDisplay(v: number | null | undefined): string {
  const n = normalizeMaterialVat(v);
  return `${n} %`;
}

export function validateMaterialEditDraft(
  draft: Partial<Material>,
  fallback: Material
): string | null {
  if (!draft.name?.trim()) return 'Name ist erforderlich.';
  if (!(draft.unit ?? '').trim()) return 'Einheit ist erforderlich.';
  const ekVal = draft.net_price ?? fallback.net_price;
  if (ekVal === null || ekVal === undefined || !Number.isFinite(Number(ekVal))) {
    return 'Einkaufspreis (EK) ist erforderlich.';
  }
  const sur = Number(draft.surcharge_percent ?? 0);
  if (!validateMaterialSurcharge(sur)) return 'Der Zuschlag darf nicht negativ sein.';
  return null;
}
